import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { BaseUserWithRole } from '@sb-tools/typescript/models/base-user-with-role.model';
import { BaseUserDTO } from '@sb-tools/typescript/models/dto/base-user.dto';
import { Field } from '@sb-tools/typescript/models/field.model';
import { ClientField } from '../../../common/models/client-field.model';
import { FormService } from '../../../common/services/form.service';
import { OutlookUserService } from '../../../common/services/outlook-user.service';
import { FormCommonDataService } from '../../../common/services/form-common-data.service';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { UserDataService } from '@sb-tools/typescript/services/user-data.service';
import { LoaderComponent } from '@sb-tools/typescript/components/loader/loader.component';
import { InlineSVGComponent } from '../../../../Frontend-Inline-SVG-Module/inline-svg.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { UserAvatarComponent } from '../../../../Frontend-User-Avatar-Module/user-avatar.component';
import { TranslateModule } from '@ngx-translate/core';
import { TruncatePipe } from '@sb-tools/typescript/pipes/truncate.pipe';
import { ClickOutsideAssignBlockDirective } from '@form-field-module/common/directives/click-outside-assign-block.directive';
import { FormFieldLabelComponent } from '@form-field-module/common/components/form-field-label/form-field-label.component';

@Component({
    selector: 'sb-assignee',
    templateUrl: './assignee.component.html',
    styleUrls: ['./assignee.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    imports: [
        LoaderComponent,
        NgSelectModule,
        FormsModule,
        InlineSVGComponent,
        TooltipModule,
        UserAvatarComponent,
        TruncatePipe,
        TranslateModule,
        ClickOutsideAssignBlockDirective,
        FormFieldLabelComponent,
    ],
})
export class AssigneeComponent implements OnInit, OnDestroy {
    @ViewChild('ngSelect') ngSelect: NgSelectComponent;

    @Input() formField: Field | ClientField;

    assignee: BaseUserWithRole;
    isLoaderHiddenWhenLoadUsersList: boolean;
    isLoadingUsersList: boolean;
    isShowAssigningSelect: boolean;
    usersToAssign: BaseUserWithRole[] | BaseUserDTO[] = [];

    get isAssignedToCurrentUser(): boolean {
        return this.assignee?.id === this.outlookUserService.user?.id;
    }

    private unsubscriber = new Subject<void>();

    constructor(
        public formService: FormService,
        private outlookUserService: OutlookUserService,
        private formCommonDataService: FormCommonDataService,
        private userDataService: UserDataService,
    ) {}

    ngOnInit() {
        this.formCommonDataService.categoryUpdatedSubject
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((categoryId: number) => {
                this.initUsersList(categoryId);
            });
    }

    closeAssigningSelect() {
        this.isShowAssigningSelect = false;
    }

    onUpdateAssignee() {
        this.formField.value = this.assignee?.id || null;
    }

    showAssigningSelect($event: Event) {
        $event.stopPropagation();

        if (!this.isShowAssigningSelect) {
            this.isShowAssigningSelect = true;
        }

        if (this.isShowAssigningSelect) {
            setTimeout(() => {
                this.ngSelect?.open();
            }, 0);
        }
    }

    unassign() {
        this.assignee = null;
        this.onUpdateAssignee();
    }

    private initUsersList(categoryId: number): void {
        this.isLoadingUsersList = true;
        this.isLoaderHiddenWhenLoadUsersList = (this.assignee && this.isAssignedToCurrentUser) || !this.assignee;

        this.userDataService
            .getUsersToAssign(categoryId)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isLoadingUsersList = false;
                }),
            )
            .subscribe((users) => {
                this.usersToAssign = this.sortUsersForAssign(users);

                if (this.assignee && !this.isAssignedToCurrentUser && !this.isAssigneeInTheTeammatesList()) {
                    this.unassign();
                }
            });
    }

    private isAssigneeInTheTeammatesList(): boolean {
        return !!this.usersToAssign.find((user) => {
            return user.id === this.assignee.id;
        });
    }

    private sortUsersForAssign(data) {
        data = [this.outlookUserService.user].concat(
            data.filter((user) => {
                return user.id !== this.outlookUserService.user.id;
            }),
        );

        data.forEach((item) => {
            item.label = item.name ? item.name + ' ' + item.email : item.email;
        });

        return data;
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
